import { useStoreState } from '@seller-hermes/hooks/easy-peasy';
import { UserTypeEnum } from '@seller-hermes/features/users/users.types';
import type { SellerGroupUser } from '@seller-hermes/features/seller-group';
import { userHasAnyOfThesePermissions } from '@seller-hermes/hooks/useUserHasAnyOfThesePermissions';

/**
 * Gate real do Carrier Hub — decisão 0.4 (fechada 2026-08-13):
 * `is_legacy_integration` é a mesma fonte de verdade usada pelo roteamento
 * do BFF (dashboard-api -> carrier-integrations, Fase 4) — não um flag
 * próprio (`carrier_hub_visible`) que poderia dessincronizar da primeira.
 * `true` (ou ausente/desconhecido) = tela legada; `false` = Hub novo.
 *
 * INTERNAL sempre vê o Hub, independente da flag — útil pra suporte/preview
 * antes da virada do seller_group. (canAccessWorkflows NÃO segue mais esse
 * padrão: lá `legacy_automation` vale pra INTERNAL também.)
 */
export const canAccessCarrierHub = (user?: SellerGroupUser | null): boolean => {
  if (!user) return false;
  if (user.type === UserTypeEnum.INTERNAL) return true;
  return user.seller_group?.is_legacy_integration === false;
};

export const useCanAccessCarrierHub = (): boolean => {
  const userType = useStoreState((s) => s.login.sellerGroupUser?.type);
  const isLegacy = useStoreState(
    (s) => s.login.sellerGroupUser?.user?.seller_group?.is_legacy_integration,
  );
  if (userType === UserTypeEnum.INTERNAL) return true;
  return isLegacy === false;
};

/**
 * Gate do Assistente de Migração (/carrier-hub/migrate).
 *
 * 2026-09-29: só INTERNAL (equipe Abbiamo) migra. Revoga a parte da decisão
 * 0.4 que liberava pro próprio usuário do seller_group; o BFF cobra o mesmo
 * (RequireAnyOfTheseUserTypes(INTERNAL) em migration/*). O resto abaixo
 * continua valendo.
 *
 * Decisão 0.4 original:
 * diferente do resto do Hub, o assistente precisa ficar acessível
 * INDEPENDENTE de `is_legacy_integration` (a flag nasce `true` pra todo
 * mundo e só vira `false` dentro do próprio complete — gatear por ela aqui
 * criaria um ovo-e-galinha). Não fica restrito a staff Abbiamo: é o próprio
 * usuário do seller_group que migra — mas precisa de
 * `update_carrier_integrations`, mesma permissão que as escritas da tela
 * legada exigem (e que o BFF cobra em POST migration/complete). Migrar é
 * irreversível; sem isso qualquer usuário do grupo conseguia disparar.
 */
export const canAccessCarrierHubMigration = (
  user?: SellerGroupUser | null,
): boolean =>
  Boolean(user) &&
  user!.type === UserTypeEnum.INTERNAL &&
  userHasAnyOfThesePermissions(['update_carrier_integrations'], user!);

export const useCanAccessCarrierHubMigration = (): boolean => {
  const user = useStoreState((s) => s.login.sellerGroupUser?.user);
  return canAccessCarrierHubMigration(user);
};
